import { useRefEffect } from '@affine/component';
import { EditorLoading } from '@affine/component/page-detail-skeleton';
import {
  BookmarkBlockService,
  customImageProxyMiddleware,
  type DocMode,
  EmbedGithubBlockService,
  EmbedLoomBlockService,
  EmbedYoutubeBlockService,
  ImageBlockService,
} from '@blocksuite/affine/blocks';
import { DisposableGroup } from '@blocksuite/affine/global/utils';
import type { AffineEditorContainer } from '@blocksuite/affine/presets';
import type { Doc } from '@blocksuite/affine/store';
import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { useService, WorkspaceService, DocService } from '@toeverything/infra';

import type { DefaultOpenProperty } from '../../doc-properties';
import { BlocksuiteEditorContainer } from './blocksuite-editor-container';
import { NoPageRootError } from './no-page-error';
import { GraphQLService } from "@affine/core/modules/cloud";

export type EditorProps = {
  page: Doc;
  mode: DocMode;
  shared?: boolean;
  defaultOpenProperty?: DefaultOpenProperty;
  // on Editor ready
  onEditorReady?: (editor: AffineEditorContainer) => (() => void) | void;
  style?: CSSProperties;
  className?: string;
};

const BlockSuiteEditorImpl = ({
  mode,
  page,
  className,
  shared,
  style,
  onEditorReady,
  defaultOpenProperty,
}: EditorProps) => {
  const workspaceService = useService(WorkspaceService);
  const docService = useService(DocService);
  const graphqlService = useService(GraphQLService);
  useEffect(() => {
    const disposable = page.slots.blockUpdated.once(() => {
      page.collection.setDocMeta(page.id, {
        updatedDate: Date.now(),
      });
    });
    return () => {
      disposable.dispose();
    };
  }, [page]);

  // let isSecond = false
  const editorRef = useRefEffect(
    (editor: AffineEditorContainer) => {
      globalThis.currentEditor = editor;
      let canceled = false;
      const disposableGroup = new DisposableGroup();

      if (onEditorReady) {
        // Invoke onLoad once the editor has been mounted to the DOM.
        editor.updateComplete
          .then(() => {
            if (canceled) {
              return;
            }
            // host should be ready

            // provide image proxy endpoint to blocksuite
            editor.host?.std.clipboard.use(
              customImageProxyMiddleware(BUILD_CONFIG.imageProxyUrl)
            );
            ImageBlockService.setImageProxyURL(BUILD_CONFIG.imageProxyUrl);

            // provide link preview endpoint to blocksuite
            BookmarkBlockService.setLinkPreviewEndpoint(
              BUILD_CONFIG.linkPreviewUrl
            );
            EmbedGithubBlockService.setLinkPreviewEndpoint(
              BUILD_CONFIG.linkPreviewUrl
            );
            EmbedYoutubeBlockService.setLinkPreviewEndpoint(
              BUILD_CONFIG.linkPreviewUrl
            );
            EmbedLoomBlockService.setLinkPreviewEndpoint(
              BUILD_CONFIG.linkPreviewUrl
            );

            return editor.host?.updateComplete;
          })
          .then(() => {
            if (canceled) {
              return;
            }
            const dispose = onEditorReady(editor);
            if (dispose) {
              disposableGroup.add(dispose);
            }
          })
          .catch(console.error);
      }

      return () => {
        // if (isSecond) {
        //   const timeDiffM = (Date.now() - docService.doc.meta$.value.createDate) / 1000
        //   // 30秒之内新增的文档退出
        //   console.log('检测', timeDiffM)
        //   if (timeDiffM < 30) {
        //     console.log('触发自动清理检测')
        //     // 判断是否为任务文档
        //     graphqlService.gql({
        //       query: getTaskByDocumentIdQuery,
        //       variables: {
        //         input: {
        //           documentIds: [page.id],
        //         }
        //       }
        //     }).then(res => {
        //       const data = res.getTaskByDocumentId[0]
        //       // 非任务文档执行删除
        //       if (!data.hasTask) {
        //         // 获取文档标题和内容
        //         const fullUrl = location.origin + `/workspace/${ workspaceService.workspace.id }/${ page.id }`
        //         // 第一步：获取文章内容
        //         getIframeContent(fullUrl).then(docResp => {
        //           const nullContent = JSON.stringify({
        //             "title": "​",
        //             "content": ""
        //           })
        //           if (JSON.stringify(docResp) == nullContent) {
        //             // 删除文档
        //             console.log('触发自动清理')
        //             deleteTemplate(page.id, workspaceService.workspace.id, graphqlService)
        //             workspaceService.workspace.docCollection.removeDoc(page.id);
        //           }
        //         })
        //       }
        //     })
        //   }
        // } else {
        //   isSecond = true
        //   console.log('进入文档', page.id)
        // }
        canceled = true;
        disposableGroup.dispose();
      };
    },
    [onEditorReady, page]
  );

  return (
    <BlocksuiteEditorContainer
      mode={mode}
      page={page}
      shared={shared}
      defaultOpenProperty={defaultOpenProperty}
      ref={editorRef}
      className={className}
      style={style}
    />
  );
};

export const BlockSuiteEditor = (props: EditorProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (props.page.root) {
      setIsLoading(false);
      return;
    }
    const timer = setTimeout(() => {
      disposable.dispose();
      setError(new NoPageRootError(props.page));
    }, 20 * 1000);
    const disposable = props.page.slots.rootAdded.once(() => {
      setIsLoading(false);
      clearTimeout(timer);
    });
    return () => {
      disposable.dispose();
      clearTimeout(timer);
    };
  }, [props.page]);

  if (error) {
    throw error;
  }

  return isLoading ? (
    <EditorLoading />
  ) : (
    <BlockSuiteEditorImpl key={props.page.id} {...props} />
  );
};
